import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  BackIcon, HumidityIcon, ThermometerIcon,
  WaterLevelIcon
} from "../../assets/icons";
import { Sensor } from "../../assets/svg/index";
import { GlobalState } from "../../GlobalState";
import TodayReport from "../report/TodayReport";
import Banner from "../utils/banner";
import Loading from "../utils/loading";
import NotFound from "../utils/not_found";
import DashboardSensorBox from "./DashboadSensorBox";
import DoubleRangeSlider from "./DoubleRangeSlider";
import { editAccess } from "../utils/utils";
import moment from "moment";
import HourlyReport from "../report/HourlyReport";
import WeeklyReportV2 from "../report/WeeklyReportV2";
import { downloadPDF } from "../utils/exportImage";
import ModernReportForm from "../utils/DatePicker";
import { literToKiloliter, capitalizeFirstLetter } from "../utils/utils";
import { Droplet, Box, Layers, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { CiSettings } from "react-icons/ci";
import { FiEdit2 } from "react-icons/fi";
import { MdClose } from "react-icons/md";

const Container = styled.div`
  margin: 0 auto;
  width: 90%;
  padding-top: 0.5rem;
  @media (min-width: 61.313em) { width: 80%; }
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 3.2fr;
  gap: 2rem;
  align-items: start;
  margin-top: 0.2em;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: center;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  @media (max-width: 900px) {
    align-items: center;
    & > * { width: 95%; }
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  & > * { width: 100%; max-width: 100%; box-sizing: border-box; }
  @media (max-width: 900px) {
    align-items: center;
    & > * { width: 95%; }
  }
`;

const BackIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  &:hover { border: 2px solid var(--primary); }
  @media (min-width: 61.313em) { width: 55px; height: 55px; }
  @media (max-width: 768px) { width: 28px; height: 28px; }
`;

/* ── Desktop only ── */
const DesktopCard = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  gap: 0.3rem;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 900px) { display: none; }
`;

/* ── Mobile only wrapper ── */
const MobileOnly = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    box-sizing: border-box;
  }
`;

const MobileCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.09);
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  max-width: 400px;
  margin: 0 auto;
`;

const MobileCardHeader = styled.div`
  padding: 0.45rem 0.7rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8rem;
  font-weight: 800;
  color: #1e3a8a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileInfoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  max-width: 360px;
  margin: 0 auto;
`;

const MobileInfoCell = styled.div`
width: 90px;
  flex: 1;
  display: flex;
  flex-grow: 0'
  flex-direction: row;
  align-items: center;
  gap: 0.3rem;
  padding: 0.45rem 0.4rem;
  border-right: ${({ last }) => last ? "none" : "1px solid #f1f5f9"};
`;

const MobileIconCircle = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MobileCellText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;
`;

const MobileCellLabel = styled.span`
  font-size: 0.75rem;
  color: #050606ff;
  font-weight: 1000;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const MobileCellValue = styled.span`
  font-size: 0.75rem;
  color: #1e293b;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Edit section */
const MobileEditSection = styled.div`
  border-top: 1px solid #f1f5f9;
  padding: 0.4rem 0.6rem;
`;

const MobileEditHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  // margin-bottom: ${({ open }) => open ? "0.5rem" : "0"};;
  margin-bottom: 0.5rem
`;

const MobileEditTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.9rem;
  font-weight: 200;
  color: #333;
  svg { color: #004080; flex-shrink: 0; }
`;

const MobileEditToggleBtn = styled.button`
  background: ${({ isEditing }) => isEditing ? "#ff4444" : "#5a82ff"};
  border: none;
  border-radius: 7px;
  padding: 0.3rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  svg { color: white; font-size: 0.85rem; }
  &:disabled { background: #c4c4c4; cursor: not-allowed; }
`;

/* Min/Max display row — always visible */
const MobileMinMaxRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
`;

const MobileMinMaxBadge = styled.div`
  flex: 1;
  background: ${({ bg }) => bg || "#f1f5f9"};
  border-radius: 8px;
  padding: 0.3rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.05rem;
`;

const MobileMinMaxLabel = styled.span`
  font-size: 0.52rem;
  color: ${({ color }) => color || "#64748b"};
  font-weight: 700;
  text-transform: uppercase;
`;

const MobileMinMaxValue = styled.span`
  font-size: 0.85rem;
  color: ${({ color }) => color || "#1e293b"};
  font-weight: 900;
`;

const MobileInputRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.6rem;              /* ← more gap between the two boxes */
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.3rem 0;        /* ← top/bottom breathing room */
`;

const MobileInputBox = styled.div`
  width: 90px;              /* ← slightly wider outer box */
  flex-shrink: 0;
  border: 1px solid #cad9ef;
  border-radius: 10px;
  padding: 0.35rem 0.5rem;  /* ← more padding inside box */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;             /* ← gap between label and input */
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  box-sizing: border-box;
  h6 {
    font-size: 0.75rem;
    font-weight: 800;
    color: #004080;
    margin: 0;
    white-space: nowrap;
    flex-shrink: 0;         /* ← prevent label from squishing */
  }
  input {
    width: 38px;
    padding: 0.2rem 0.15rem;
    border-radius: 6px;
    border: 1px solid #ccc;
    text-align: center;
    font-size: 0.9rem;
    font-weight: 800;
    color: #1e40af;
    background: #fff;
    box-sizing: border-box;
    &:focus { border-color: #5a82ff; outline: none; }
    &:disabled { background: #f4f4f4; color: #999; cursor: not-allowed; }
  }
`;

const MobileUpdateBtn = styled.button`
  background: #5a82ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.3rem 0.8rem;
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  align-self: stretch;
  display: flex;
  align-items: center;
  &:disabled { background: #c4c4c4; cursor: not-allowed; }
`;

const MobileUpdateIconBtn = styled.button`
  background: ${({ disabled }) => disabled ? "#c4c4c4" : "#5a82ff"};
  color: #fff;
  border: none;
  border-radius: 8px;
  width: 36px;           /* ← fixed width */
  height: 36px;          /* ← fixed height */
  padding: 0;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/* Mobile Water Sense */
const MobileWaterSenseCard = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.09);
    padding: 0.6rem 0.8rem;
    gap: 0.6rem;
    width: 100%;
    max-width: 250px;        /* ← add this */
    margin: 0 auto;          /* ← add this to center it */
    box-sizing: border-box;
  }
`;

const FlowDot = styled.span`
  font-size: 1.2rem;
  color: ${({ flowing }) => flowing ? "#22c55e" : "#ef4444"};
  flex-shrink: 0;
`;

/* ─── Chart Carousel ─── */
const ChartCarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  background: transparent;
`;


const ChartSlideContainer = styled.div`
  overflow: hidden;
  width: 100%;
  touch-action: pan-y;
  background: transparent;
`;

const ChartDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.4rem;    /* ← small gap below chart */
`;

const ChartDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ active }) => active ? "#667eea" : "#e2e8f0"};
  transition: background 0.2s ease;
  cursor: pointer;
`;

const ChartSlideTrack = styled.div`
  display: flex;
  width: 200%;
  transition: transform 0.35s ease;
  transform: ${({ index }) => `translateX(-${index * 50}%)`};
  background: transparent;
`;

const ChartSlide = styled.div`
  width: 50%;
  flex-shrink: 0;
   box-sizing: border-box;
   background: transparent;
`;

/* Desktop arrow nav */
// const DesktopChartNav = styled.div`
//   display: none;
//   @media (min-width: 900px) {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     margin-bottom: 0.5rem;
//   }
// `;

const DesktopChartNav = styled.div`
  display: none;
  @media (min-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;     /* ← overlay on chart */
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    pointer-events: none;   /* ← don't block chart interactions */
    z-index: 10;
    padding: 0 0.3rem;
  }
`;

const ChartNavBtn = styled.button`
  background: rgba(255,255,255,0.9);
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.3rem 0.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  pointer-events: all;      /* ← re-enable clicks on buttons only */
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: #f1f5f9; border-color: #667eea; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

// const ChartNavBtn = styled.button`
//   background: #fff;
//   border: 1.5px solid #e2e8f0;
//   border-radius: 8px;
//   padding: 0.3rem 0.6rem;
//   cursor: pointer;
//   display: flex;
//   align-items: center;
//   gap: 0.3rem;
//   font-size: 0.75rem;
//   font-weight: 600;
//   color: #334155;
//   transition: all 0.2s ease;
//   &:hover:not(:disabled) { background: #f1f5f9; border-color: #667eea; color: #667eea; }
//   &:disabled { opacity: 0.35; cursor: not-allowed; }
// `;

const ChartTabLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: #667eea;
`;

// const ChartDots = styled.div`
//   display: flex;
//   justify-content: center;
//   gap: 0.4rem;
//   margin-top: 0.5rem;
//   @media (min-width: 900px) { display: none; }
// `;

// const ChartDot = styled.div`
//   width: 7px;
//   height: 7px;
//   border-radius: 50%;
//   background: ${({ active }) => active ? "#667eea" : "#e2e8f0"};
//   transition: background 0.2s ease;
// `;


export default function SensorData({ match }) {
  const state = useContext(GlobalState);
  const history = useHistory();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [ValidSensor, setValidSensor] = useState(true);
  const [values, setValues] = useState({ wait_time: "" });
  const [timePeriod, setTimePeriod] = useState("today");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minDate] = useState(moment().subtract(1, 'years').format('YYYY-MM-DD'));
  const [maxDate] = useState(moment().format('YYYY-MM-DD'));
  const [sensorReading, setSensorReading] = useState(null);
  const [liveReading, setLiveReading] = useState(null);
  const [progress, setProgress] = useState(0);
  const [mobileIsEditing, setMobileIsEditing] = useState(false);
  const [mobileValues, setMobileValues] = useState({ lower: 0, upper: 0 });
  const [mobileErr, setMobileErr] = useState("");
  const [chartIndex, setChartIndex] = useState(0);
  const componentRefs = useRef({});

  // swipe
  const touchStartX = useRef(null);
  const slideRef = useRef(null);

  const sensor_id = match.params.sensor_id;
  const sensor_type = match.params.sensor_type;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const submitMotorUpdate = async (e) => {
    e.preventDefault();
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/sensor/${sensor_id}`,
      { wait_time: values.wait_time },
      { headers: { Authorization: state.token[0] } }
    );
    if (res.status === 200) alert("Updated successfully.");
  };

  const handleMobileEditToggle = () => {
    if (mobileIsEditing) {
      setMobileValues({ lower: parseInt(data.lower_limit), upper: parseInt(data.upper_limit) });
    }
    setMobileIsEditing(prev => !prev);
  };

  const handleMobileUpdate = async () => {
    if (mobileValues.upper <= mobileValues.lower) {
      setMobileErr("Max must be > Min");
      setTimeout(() => setMobileErr(""), 3000);
      return;
    }
    setMobileErr("Updating...");
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API}/sensor/update_limit?sensor_id=${sensor_id}`,
        { lower_limit: mobileValues.lower, upper_limit: mobileValues.upper },
        { headers: { Authorization: state.token[0] } }
      );
      if (res.status === 200) { setMobileErr("Updated!"); setMobileIsEditing(false); }
    } catch { setMobileErr("Error"); }
    setTimeout(() => setMobileErr(""), 3000);
  };

  // touch swipe handlers
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && chartIndex < 1) setChartIndex(1);
      if (diff < 0 && chartIndex > 0) setChartIndex(0);
    }
    touchStartX.current = null;
  };

  useEffect(() => {
    async function fetchData(token) {
      try {
        const sensorData = await axios.get(
          `${process.env.REACT_APP_API}/sensor/data?sensor_id=${sensor_id}`,
          { headers: { Authorization: token[0] } }
        );
        if (sensorData) {
          setData(sensorData.data);
          setValues({ wait_time: sensorData.data.wait_time });
          setMobileValues({
            lower: parseInt(sensorData.data.lower_limit) || 0,
            upper: parseInt(sensorData.data.upper_limit) || 0,
          });

          if (sensor_type === "Water Sense") {
            const reading = await axios.get(
              `${process.env.REACT_APP_API}/sensor/reading?sensor_id=${sensor_id}&&type=${sensor_type}`,
              { headers: { Authorization: token[0] } }
            );
            setSensorReading(reading.data);
          }

          if (sensor_type === "Water Level" || sensor_type === "Diesel Level") {
            const reading = await axios.get(
              `${process.env.REACT_APP_API}/sensor/reading?sensor_id=${sensor_id}&&type=${sensor_type}`,
              { headers: { Authorization: token[0] } }
            );
            setLiveReading(reading.data);
          }

          const today = moment().format("YYYY-MM-DD");
          setStartDate(today);
          setEndDate(today);
          setLoading(false);
        }
      } catch (err) { setValidSensor(false); console.log(err); }
    }
    fetchData(state.token);
  }, [state.token, sensor_id]);

  const addRef = (id, ref) => { if (ref) componentRefs.current[id] = ref; };

  if (!ValidSensor) return <NotFound />;
  if (loading) return <Loading />;

  const isFlowing = parseInt(sensorReading?.value) === 1;
  const amountOfWater = liveReading
    ? (liveReading.unit === "litre" ? literToKiloliter(liveReading.value) : parseFloat(liveReading.value).toLocaleString())
    : "—";

  const showTwoCharts = (timePeriod === "today" || timePeriod === "custom") && sensor_type !== "Water Sense";
  const chartLabels = ["Trend Graph", "Hourly Report"];

  return (
    <Container>
      {/* Banner */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", width: "100%", marginTop: "0" }}>
        <BackIconWrapper onClick={() => history.push("/")} style={{ flexShrink: 0 }}>
          <BackIcon />
        </BackIconWrapper>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Banner title={"Detailed Insights !"} subtitle={"View the sensor's performance through trend graphs, water consumption details, and fill level patterns."}>
            <Sensor />
          </Banner>
        </div>
      </div>

      <GridLayout>
        <LeftColumn>

          <DesktopCard>
            {data.sensor_id && (
              <DashboardSensorBox sensor_id={sensor_id} type={sensor_type} dimension={data.dimension} />
            )}
            {data.type.includes("Humidity") && (
              <>
                {sensor_type === "Humidity" && (
                  <DoubleRangeSlider name="Humidity Min/Max Setting"
                    defautvalues={[parseInt(data.lower_limit_2), parseInt(data.upper_limit_2)]}
                    disabled={!editAccess(state.userAPI)} type="humidity" sensor_id={sensor_id} />
                )}
                {sensor_type === "Temperature" && (
                  <DoubleRangeSlider name="Temperature Min/Max Setting"
                    defautvalues={[parseInt(data.lower_limit_1), parseInt(data.upper_limit_1)]}
                    disabled={!editAccess(state.userAPI)} type="temperature" sensor_id={sensor_id} />
                )}
              </>
            )}
            {data.type.includes("Water Level") && (
              <DoubleRangeSlider name="Water Level (%)"
                defautvalues={[parseInt(data.lower_limit), parseInt(data.upper_limit)]}
                disabled={!editAccess(state.userAPI)} sensor_id={sensor_id} />
            )}
            {sensor_type === "Water Sense" && (
              <div style={{ borderRadius: "8px", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.8rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "60%", background: "rgba(0,0,0,0.04)", padding: "0.5rem 0.6rem", borderRadius: "6px" }}>
                  <span style={{ fontWeight: 800, fontSize: "0.85rem", color: isFlowing ? "#22c55e" : "#ef4444" }}>
                    {isFlowing ? "● Flowing" : "● No Flow"}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "#e3f2fd", padding: "0.5rem 0.6rem", borderRadius: "6px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <span style={{ color: "#1565c0", fontSize: "0.9rem", fontWeight: "600" }}>Wait Time</span>
                    <span style={{ color: "#6b7280", fontSize: "0.65rem" }}>(min)</span>
                  </div>
                  <input disabled={!editAccess(state.userAPI)} name="wait_time" value={values.wait_time}
                    onChange={handleInputChange} min={1} max={999} type="number"
                    style={{ width: "70px", padding: "0.35rem", borderRadius: "6px", border: "1px solid #90caf9", textAlign: "center", fontSize: "0.9rem", color: "#0d47a1" }} />
                </div>
                <button disabled={!editAccess(state.userAPI)} onClick={submitMotorUpdate}
                  style={{ background: "#1565c0", color: "#fff", border: "none", borderRadius: "6px", padding: "0.4rem 1.2rem", cursor: "pointer", fontWeight: "600", fontSize: "0.85rem" }}>
                  Update
                </button>
              </div>
            )}
          </DesktopCard>

          {/* ══ MOBILE — Water Level ══ */}
          {data.type.includes("Water Level") && (
            <MobileOnly>
              <MobileCard>
                <MobileCardHeader>{data.location_name || data.location}</MobileCardHeader>

                {/* Amount | Shape | Capacity */}
                <MobileInfoRow>
                  <MobileInfoCell>
                    <MobileIconCircle><Droplet size={13} color="#fff" /></MobileIconCircle>
                    <MobileCellText>
                      <MobileCellLabel>Amount</MobileCellLabel>
                      <MobileCellValue>{amountOfWater}</MobileCellValue>
                    </MobileCellText>
                  </MobileInfoCell>
                  <MobileInfoCell>
                    <MobileIconCircle><Box size={13} color="#fff" /></MobileIconCircle>
                    <MobileCellText>
                      <MobileCellLabel>Shape</MobileCellLabel>
                      <MobileCellValue>{capitalizeFirstLetter(data.dimension?.tank_shape ?? "—")}</MobileCellValue>
                    </MobileCellText>
                  </MobileInfoCell>
                  <MobileInfoCell last>
                    <MobileIconCircle><Layers size={13} color="#fff" /></MobileIconCircle>
                    <MobileCellText>
                      <MobileCellLabel>Capacity</MobileCellLabel>
                      <MobileCellValue>{literToKiloliter(data.dimension?.tank_volume || 0)}</MobileCellValue>
                    </MobileCellText>
                  </MobileInfoCell>
                </MobileInfoRow>

                {/* Edit section */}
                <MobileEditSection>
                  {/* <MobileEditHeader open={mobileIsEditing}>
                    <MobileEditTitle>
                      <CiSettings size={18} />
                      <span>Water Level (%)</span>
                    </MobileEditTitle>
                    <MobileEditToggleBtn
                      disabled={!editAccess(state.userAPI)}
                      isEditing={mobileIsEditing}
                      onClick={handleMobileEditToggle}
                      title={mobileIsEditing ? "Cancel" : "Edit"}
                    >
                      {mobileIsEditing ? <MdClose /> : <FiEdit2 />}
                    </MobileEditToggleBtn>
                  </MobileEditHeader> */}

                  <MobileEditHeader open={mobileIsEditing}>
                    <MobileEditTitle>
                      <CiSettings size={18} />
                      <span>Water Level (%)</span>
                    </MobileEditTitle>
                    <MobileEditToggleBtn
                      disabled={!editAccess(state.userAPI)}
                      isEditing={mobileIsEditing}
                      onClick={handleMobileEditToggle}
                      title={mobileIsEditing ? "Cancel" : "Edit"}
                    >
                      {mobileIsEditing ? <MdClose /> : <FiEdit2 />}
                    </MobileEditToggleBtn>
                  </MobileEditHeader>

                  {/* Edit inputs — only shown when editing */}
                  <MobileInputRow>
                    <MobileInputBox>
                      <input
                        type="number"
                        value={mobileValues.lower}
                        onChange={(e) => { const v = Number(e.target.value); if (v >= 0 && v <= 100) setMobileValues(p => ({ ...p, lower: v })); }}
                        min="0" max="100"
                        disabled={!mobileIsEditing}
                      />
                      <h6>Min %</h6>
                    </MobileInputBox>
                    <MobileInputBox>
                      <input
                        type="number"
                        value={mobileValues.upper}
                        onChange={(e) => { const v = Number(e.target.value); if (v >= 0 && v <= 100) setMobileValues(p => ({ ...p, upper: v })); }}
                        min="0" max="100"
                        disabled={!mobileIsEditing}
                      />
                      <h6>Max %</h6>
                    </MobileInputBox>
                    {/* <MobileUpdateBtn onClick={handleMobileUpdate} disabled={!mobileIsEditing || !editAccess(state.userAPI)}>
                      {mobileErr || "Update"}
                    </MobileUpdateBtn> */}
                    <MobileUpdateIconBtn
                      onClick={handleMobileUpdate}
                      disabled={!mobileIsEditing || !editAccess(state.userAPI)}
                      title="Update"
                    >
                      <Check size={18} />
                    </MobileUpdateIconBtn>
                  </MobileInputRow>
                </MobileEditSection>
              </MobileCard>
            </MobileOnly>
          )}

          {sensor_type === "Water Sense" && (
            <MobileWaterSenseCard>
              <FlowDot flowing={isFlowing}>●</FlowDot>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.05rem" }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 800, color: isFlowing ? "#16a34a" : "#dc2626" }}>
                  {isFlowing ? "Flowing" : "No Flow"}
                </span>
                <span style={{ fontSize: "0.58rem", color: "#64748b", fontWeight: 600 }}>Water Sense</span>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span style={{ fontSize: "0.6rem", color: "#64748b", fontWeight: 600 }}>Wait</span>
                  <span style={{ fontSize: "0.55rem", color: "#94a3b8" }}>(min)</span>
                </div>
                <input disabled={!editAccess(state.userAPI)} name="wait_time" value={values.wait_time}
                  onChange={handleInputChange} min={1} max={999} type="number"
                  style={{ width: "48px", padding: "0.25rem", borderRadius: "6px", border: "1px solid #90caf9", textAlign: "center", fontSize: "0.8rem", color: "#0d47a1" }} />
                <button disabled={!editAccess(state.userAPI)} onClick={submitMotorUpdate}
                  style={{ background: "#1565c0", color: "#fff", border: "none", borderRadius: "6px", padding: "0.3rem 0.55rem", cursor: "pointer", fontWeight: 700, fontSize: "0.62rem" }}>
                  Save
                </button>
              </div>
            </MobileWaterSenseCard>
          )}

          {data.type.includes("Humidity") && (
            <MobileOnly>
              <MobileCard>
                <MobileCardHeader>{data.location_name || data.location}</MobileCardHeader>
                <div style={{ padding: "0.4rem 0.5rem" }}>
                  {sensor_type === "Humidity" && (
                    <DoubleRangeSlider name="Humidity"
                      defautvalues={[parseInt(data.lower_limit_2), parseInt(data.upper_limit_2)]}
                      disabled={!editAccess(state.userAPI)} type="humidity" sensor_id={sensor_id} />
                  )}
                  {sensor_type === "Temperature" && (
                    <DoubleRangeSlider name="Temperature"
                      defautvalues={[parseInt(data.lower_limit_1), parseInt(data.upper_limit_1)]}
                      disabled={!editAccess(state.userAPI)} type="temperature" sensor_id={sensor_id} />
                  )}
                </div>
              </MobileCard>
            </MobileOnly>
          )}

        </LeftColumn>

        <RightColumn>
          {/* Date picker */}
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.6rem", width: "100%", flexWrap: "wrap" }}>
            <ModernReportForm
              timePeriod={timePeriod} setTimePeriod={setTimePeriod}
              startDate={startDate} setStartDate={setStartDate}
              endDate={endDate} setEndDate={setEndDate}
              minDate={minDate} maxDate={maxDate} progress={progress}
              onDownload={async () => {
                const uniqueElements = Object.values(componentRefs.current).filter(el => el !== null && el !== undefined);
                if (uniqueElements.length === 0) { alert("No report available to download!"); return; }
                await new Promise(resolve => setTimeout(resolve, 2000));
                downloadPDF(uniqueElements, setProgress);
              }}
              disabled={!startDate || !endDate}
              allowedPeriods={sensor_type === "Water Sense" ? ["today", "custom"] : ["today", "custom", "custom-week", "custom-month"]}
            />
          </div>

          {/* ── Today / Custom: carousel ── */}
          {(timePeriod === "today" || timePeriod === "custom") && startDate && endDate && data.sensor_id && (
            showTwoCharts ? (
              <ChartCarouselWrapper>
                {/* Desktop arrow nav */}
                {/* <DesktopChartNav>
                  <ChartNavBtn onClick={() => setChartIndex(0)} disabled={chartIndex === 0}>
                    <ChevronLeft size={15} />
                  </ChartNavBtn>
                  <ChartNavBtn onClick={() => setChartIndex(1)} disabled={chartIndex === 1}>
                  <ChevronRight size={15} />
                  </ChartNavBtn>
                </DesktopChartNav> */}

                {/* Slide track */}
                <ChartSlideContainer
                  ref={slideRef}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <ChartSlideTrack index={chartIndex}>
                    <ChartSlide>
                      <TodayReport
                        ref={(el) => addRef(`today_${data.sensor_id}`, el)}
                        sensor_id={data.sensor_id} sensor_type={data.type}
                        toShow={data.type === "Humidity" ? "Temperature" : data.type}
                        startDate={startDate} endDate={endDate}
                        location={data.location_name || data.location}
                        motor_id={data.motor_id}
                      />
                    </ChartSlide>
                    <ChartSlide>
                      <HourlyReport
                        ref={(el) => addRef(`hourly_${data.sensor_id}`, el)}
                        sensor_id={data.sensor_id} sensor_type={data.type}
                        startDate={startDate} endDate={endDate}
                        location={data.location_name || data.location}
                        reportType={data.location && data.location.toLowerCase().includes("sump") ? "Filled" : "Consumption"}
                        timePeriod={timePeriod}
                      />
                    </ChartSlide>
                  </ChartSlideTrack>
                </ChartSlideContainer>

                {/* Mobile dots */}
                <ChartDots>
                  {chartLabels.map((_, i) => (
                    <ChartDot key={i} active={chartIndex === i} onClick={() => setChartIndex(i)} />
                  ))}
                </ChartDots>
              </ChartCarouselWrapper>
            ) : (
              /* Water Sense — only one chart */
              <TodayReport
                ref={(el) => addRef(`today_${data.sensor_id}`, el)}
                sensor_id={data.sensor_id} sensor_type={data.type}
                toShow={data.type === "Humidity" ? "Temperature" : data.type}
                startDate={startDate} endDate={endDate}
                location={data.location_name || data.location}
                motor_id={data.motor_id}
              />
            )
          )}

          {/* Week / Month view */}
          {(timePeriod === "custom-week" || timePeriod === "custom-month") && data.sensor_id && startDate && endDate && (
            <WeeklyReportV2
              ref={(el) => addRef(`weekly_${data.sensor_id}`, el)}
              key={`${data.sensor_id}_${startDate}_${endDate}`}
              sensor_id={data.sensor_id} sensor_type={data.type}
              location={data.location_name || data.location}
              startDate={startDate} endDate={endDate}
              reportType={data.location && data.location.toLowerCase().includes("sump") ? "Filled" : "Consumption"}
              timePeriod={timePeriod}
            />
          )}
        </RightColumn>
      </GridLayout>
    </Container>
  );
}